/**
 * Shapes shared by the native Executive Overview (/overview), spec
 * docs/superpowers/specs/2026-10-14-native-executive-overview-design.md. Pure types.
 * NULL is "not measurable", never 0, in every number below.
 *
 * @module services/overview/overviewTypes
 */
import type { DateRange } from '@/services/looker/lookerDateRange';

/** One get_overview_facts row (migration 121), normalised. */
export interface FactRow {
  date: string;
  workspaceId: string;
  platform: string;
  /** 'unspecified' when the campaign has none. */
  objective: string;
  accountId: string | null;
  accountName: string | null;
  /** null = the account's currency is unknown: excluded from every number and flagged. */
  currency: string | null;
  spend: number | null;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
  conversionValue: number | null;
  reach: number | null;
}

/** The two facts reads (R1): the period and the equal period before it. */
export interface FactPair {
  current: FactRow[];
  previous: FactRow[];
}

export interface OverviewWindows {
  /** The filter-bar token ('28d', 'custom:A:B', …). */
  token: string;
  current: DateRange;
  previous: DateRange;
  /** Result's window (ends today − 3); null = no day qualifies. Always inside `current`. */
  cut: DateRange | null;
  /** What the Result Δ compares with; null with `cut`. Always inside `previous`. */
  cutPrevious: DateRange | null;
  /** The last day Result counts. */
  through: string;
  /** Result's window ends before the period does: the "ตัด 3 วันล่าสุด" chip. */
  cutApplied: boolean;
}

/**
 * Why a Result figure holds what it holds (I2): 'value' = counted; 'notReported' =
 * rows of the objective with spend > 0 exist in the Result window and none holds a
 * value for the metric (NULL, or a reach the platform did not send: reportedMetric)
 * ("ไม่ได้รายงาน"); 'none' = no such rows, incl. no Result window ("—").
 */
export type ResultState = 'value' | 'notReported' | 'none';

export type ObjectiveFilter = 'all' | 'awareness' | 'consideration' | 'conversion';
export const OBJECTIVE_FILTERS: readonly ObjectiveFilter[] = ['all', 'awareness', 'consideration', 'conversion'];

/** What one read settled to. idle = not asked for (all workspaces, or no scope yet). */
export type ReadState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'failed' };

/** What one box draws. needsWorkspace = a workspace-bound box in the all-workspaces view. */
export type BoxState<T> =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'empty' }
  | { status: 'needsWorkspace' }
  | { status: 'ready'; data: T };

export interface CurrencyChoice {
  /** Currencies with spend > 0 in the current window, most spent first. */
  currencies: string[];
  /** The one every number is in; null = no known currency at all. */
  currency: string | null;
  /** Spend > 0 sat on accounts of unknown currency and is in no number. */
  unknownExcluded: boolean;
}

export interface Totals {
  spend: number | null;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
  conversionValue: number | null;
  reach: number | null;
}
